import React, { useMemo } from 'react';
import { ScrollView, Text, TouchableOpacity, View } from 'react-native';
import { DateTime } from 'luxon';
import { StyleSheet } from 'react-native-unistyles';

import CutCard from '@/components/General/CutCard';
import DatePickerInput from '@/components/General/inputs/DatePickerInput';

const AnalyticsDateRangeControls = ({
    presets,
    selectedPreset,
    startDate,
    endDate,
    onPresetChange,
    onStartDateChange,
    onEndDateChange,
    isNative,
    maximumEndDate = new Date(),
    testIDPrefix = 'analytics-date-range',
    children,
}) => {
    const rangeLabel = useMemo(() => {
        if (selectedPreset && selectedPreset !== 'custom') {
            return presets.find(preset => preset.value === selectedPreset)?.label ?? 'Custom range';
        }
        if (startDate && endDate) {
            return `${DateTime.fromJSDate(startDate).toFormat('MMM d, yyyy')} – ${DateTime.fromJSDate(endDate).toFormat('MMM d, yyyy')}`;
        }
        return '';
    }, [presets, selectedPreset, startDate, endDate]);

    return (
        <CutCard
            accentColor="primary"
            style={styles.controlPanel}
            contentStyle={styles.controlPanelContent}
            testID={`${testIDPrefix}-controls`}
        >
            <View style={styles.controlPanelHeader}>
                <Text style={styles.controlPanelLabel}>Date Range</Text>
                <Text style={styles.controlPanelRange}>{rangeLabel}</Text>
            </View>

            <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={styles.presetsContainer}
                style={isNative ? styles.presetsScrollNative : styles.presetsScrollWeb}
                keyboardShouldPersistTaps="handled"
            >
                {presets.map((preset) => (
                    <TouchableOpacity
                        key={preset.value}
                        style={[
                            styles.presetPill,
                            selectedPreset === preset.value && styles.presetPillActive,
                        ]}
                        onPress={() => onPresetChange(preset.value)}
                        testID={`${testIDPrefix}-preset-${preset.value}`}
                        accessibilityRole="button"
                        accessibilityLabel={preset.label}
                        accessibilityState={{ selected: selectedPreset === preset.value }}
                    >
                        <Text style={[
                            styles.presetPillText,
                            selectedPreset === preset.value && styles.presetPillTextActive,
                        ]}>
                            {preset.label}
                        </Text>
                    </TouchableOpacity>
                ))}
            </ScrollView>

            <View style={styles.filtersRow}>
                <View style={styles.filterItem} testID={`${testIDPrefix}-start-date`}>
                    <DatePickerInput
                        title="Start Date"
                        mode="date"
                        value={startDate}
                        maximumDate={endDate}
                        onUpdate={onStartDateChange}
                        hideClearIcon
                    />
                </View>
                <View style={styles.filterItem} testID={`${testIDPrefix}-end-date`}>
                    <DatePickerInput
                        title="End Date"
                        mode="date"
                        value={endDate}
                        minimumDate={startDate}
                        maximumDate={maximumEndDate}
                        onUpdate={onEndDateChange}
                        hideClearIcon
                    />
                </View>
                {children ? <View style={styles.filterActions}>{children}</View> : null}
            </View>
        </CutCard>
    );
};

const styles = StyleSheet.create(({ colors, fonts, shadows }, rt) => ({
    controlPanel: {
        padding: 16,
        // CutCard owns surface/radius; the shadow stays here so it converts
        // to a clip-path drop-shadow on web.
        boxShadow: shadows.card,
    },
    controlPanelContent: {
        gap: 14,
    },
    controlPanelHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 6,
    },
    controlPanelLabel: {
        fontSize: 12,
        fontFamily: fonts.heading,
        color: colors.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: 0.6,
    },
    controlPanelRange: {
        fontSize: 14,
        fontFamily: fonts.bold,
        color: colors.primary,
    },
    presetsScrollNative: {
        flexGrow: 0,
        marginHorizontal: -16,
        paddingHorizontal: 16,
    },
    presetsScrollWeb: {
        flexGrow: 0,
    },
    presetsContainer: {
        flexDirection: 'row',
        gap: 8,
        paddingVertical: 2,
    },
    presetPill: {
        paddingHorizontal: 14,
        paddingVertical: 8,
        borderRadius: 20,
        backgroundColor: colors.background,
        _web: {
            transition: 'background-color 0.2s ease-in-out',
            _hover: {
                backgroundColor: colors.hoverStrong,
            },
        },
    },
    presetPillActive: {
        backgroundColor: colors.primary,
        _web: {
            _hover: {
                backgroundColor: colors.primary,
            },
        },
    },
    presetPillText: {
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.text,
    },
    presetPillTextActive: {
        color: colors.onPrimary,
        fontFamily: fonts.heading,
    },
    filtersRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 12,
        alignItems: 'flex-end',
    },
    filterItem: {
        flexGrow: rt.isPortrait ? 1 : 0,
    },
    filterActions: {
        // No marginBottom: filtersRow is alignItems:'flex-end', so this row's
        // margin box must match the date inputs' (label + 44 input + spacing
        // sm bottom margin). An extra margin here pushed embedded selectors
        // ~10-20px above the date inputs' baseline.
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexGrow: 1,
        flexBasis: 300,
        gap: 12,
    },
}));

export default React.memo(AnalyticsDateRangeControls);
